import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../composants/Button/Button';
import Banner from '../../../composants/Banner/Banner';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import OperationPreparationBC from './OperationPreparationBC';
import CommandesComplementaires from './CommandesComplementaires';
import MailPreview from '../../../composants/MailPreview/MailPreview';
import {
  buildBonCommandeMailPreview,
  buildExpirationMailPreview,
} from '../../../composants/MailPreview/bonCommandeMailTemplate';
import { ReactComponent as PdfIcon } from '../../../assets/icons/pdf.svg';
import { OperationBC, CommandeRang, StatusId } from '../types';
import { telechargerBonCommande } from '../services/operationBcService';
import { formatOffreArticles, totalOffreArticles } from '../domain/operationOffre';
import { commandeDuRang, premierRang } from '../domain/rangs';
import { operationActionsInternals } from '../domain/operationActions';
import { NextTabRender } from '../domain/operationNextTab';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import { getStatusColor } from '../../../utils/entityUtils';
import './OperationNotification.css';

const { isRefuseOuExpire } = operationActionsInternals;

interface OperationNotificationProps {
  operation: OperationBC;
  // CTA de navigation vers l'onglet suivant, resolu par OperationContent. Pas de resolveur ici
  // (contrairement aux onglets Consultation/Offre) : cet onglet ne propose aucune action orange
  // de workflow, le telechargement du bon de commande signe n'en etant pas une.
  nextTab?: NextTabRender;
}

// Onglet "Notification et signature" ACHETEUR-BC (V2). Actif UNIQUEMENT quand le bon de commande est
// accepte/signe des deux cotes (operation au statut Valide=7, gate en domaine operationTabs). Miroir
// du "Notifications et signatures" du MS, mais ISOLE : aucun import depuis les modules MS. On y montre
// la previsualisation PDF du bon de commande signe (iframe) + le telechargement, et l'apercu du mail de
// notification envoye au candidat (prestataire). Sections pliables, comme les autres onglets acheteur.
const OperationNotification: React.FC<OperationNotificationProps> = ({ operation, nextTab }) => {
  // Ref d'impression LOCALE : le parent ne passe pas de printRef a cet onglet (contrairement a
  // Consultation et Offre). Elle couvre tout le contenu de l'onglet, bandeau d'etat compris.
  const printRef = useRef<HTMLDivElement>(null);
  // external_id = numero du document (le backend BC lit le numero, pas l'id operation) ; fallback id.
  const externalId = operation.numero ?? operation.id;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [isMailOpen, setIsMailOpen] = useState(false);

  // Blob du bon de commande signe -> URL objet pour l'iframe de previsualisation (revoquee au demontage).
  useEffect(() => {
    let annule = false;
    let objectUrl: string | null = null;
    setPdfError(false);
    setPdfUrl(null);
    telechargerBonCommande(externalId)
      .then((blob) => {
        if (annule) return;
        objectUrl = window.URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      })
      .catch(() => {
        if (!annule) setPdfError(true);
      });
    return () => {
      annule = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [externalId]);

  const dateTime = (v?: string | null): string =>
    v
      ? `${new Date(v).toLocaleDateString('fr-FR')} à ${new Date(v).toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
        })}`
      : '';

  // Commande acceptee (rang retenu) : porte le titulaire signataire + la date d'acceptation.
  const cmdAcceptee = (operation.commandes || []).find((c) => c.id_status === StatusId.Valide);
  const rang = Number(cmdAcceptee?.rang ?? premierRang(operation));
  const prestaNom = cmdAcceptee?.contrat?.entite?.nom || operation.prestataire?.nom || 'Le prestataire';
  // Interlocuteur affiche dans le mail = le contact du PRESTATAIRE (le back template 12 le derive du
  // prestataire via commande.type_compte/id_user, pas de l'acheteur). Meme source que le "candidat".
  // "Mail" du tableau = l'email du DESTINATAIRE (le prestataire retenu) : c'est la que part la notification.
  const entiteAcc = cmdAcceptee?.contrat?.entite as
    | { interlocuteur?: { email_contact?: string } }
    | undefined;
  const candidatMail = entiteAcc?.interlocuteur?.email_contact || '—';
  // Interlocuteur affiche DANS LE CORPS du mail = le CONTACT OPERATIONNEL ACHETEUR (celui que le
  // prestataire doit joindre). Cote acheteur, le back (BdCController::show) le met dans
  // organisme.acheteur (resolu depuis operation.id_user/type_compte) ; repli sur le contact top-level
  // (present cote presta via showById) puis le signataire acheteur.
  const orgAcheteur = (operation.organisme as
    | { acheteur?: { prenom?: string; nom?: string; telephone?: string } }
    | undefined)?.acheteur;
  const acheteurSig = (operation as {
    acheteur_signataire?: { prenom?: string; nom?: string; telephone?: string };
  }).acheteur_signataire;
  const interlocuteur =
    [orgAcheteur?.prenom, orgAcheteur?.nom].filter(Boolean).join(' ') ||
    [operation.acheteur?.prenom, operation.acheteur?.nom].filter(Boolean).join(' ') ||
    [acheteurSig?.prenom, acheteurSig?.nom].filter(Boolean).join(' ');
  const interlocuteurTel =
    orgAcheteur?.telephone || operation.acheteur?.telephone || acheteurSig?.telephone || '';
  const accepteLe = cmdAcceptee?.updated_at ? dateTime(cmdAcceptee.updated_at) : null;

  // Montant total HT de l'offre acceptee (meme calcul que l'onglet Offre acheteur).
  const montantFr = totalOffreArticles(formatOffreArticles(operation, rang)).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Mail Brevo 12 (notification du bon de commande au prestataire) rendu en apercu.
  const mailHtml = buildBonCommandeMailPreview({
    operationNumero: String(operation.numero ?? operation.id),
    operationDate: dateTime(operation.created_at) || accepteLe || '',
    operationNom: operation.nom || "l'opération",
    montant: montantFr,
    acceptationDate: accepteLe || '',
    interlocuteurName: interlocuteur,
    interlocuteurPhone: interlocuteurTel,
    dateEnvoi: accepteLe,
  });

  const handleDownload = useCallback(async () => {
    setInlineError(null);
    try {
      const blob = await telechargerBonCommande(externalId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const rawNumero = String(operation.numero ?? operation.id ?? 'document');
      const numeroSansP = rawNumero.startsWith('P') ? rawNumero.substring(1) : rawNumero;
      const annee = process.env.REACT_APP_ANNEE_EDITION || '25';
      link.download = `${annee}${numeroSansP} (signed).pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setInlineError(
        error instanceof Error
          ? error.message
          : 'Erreur lors du téléchargement du bon de commande. Veuillez réessayer.',
      );
    }
  }, [externalId, operation]);

  // Impression d'un mail (comme le MS) : iframe cachee sandbox (sans allow-scripts) -> print.
  // Prend le HTML en PARAMETRE : deux mails sont imprimables ici (la notification et l'expiration
  // d'un non-retenu). Une seconde copie de cette fonction figee sur l'autre HTML aurait diverge.
  const handlePrintHtml = useCallback((html: string) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('sandbox', 'allow-same-origin allow-modals');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      window.setTimeout(() => document.body.removeChild(iframe), 100);
    };
  }, []);

  // NON-RETENUS : les rangs ecartes de la cascade. `isRefuseOuExpire` (domaine) et non un test sur le
  // seul statut Expire(9) : en base, un rang ecarte porte 8 (refuse) OU 9 (expire) — 224 et 208 lignes
  // au 2026-07-17. Filtrer sur 9 seul laissait le bloc vide sur la plupart des operations.
  // Le template 13 couvre bien les deux : "votre refus ou votre absence de reponse".
  // Rien a declencher ici : le back envoie ce mail au fil de l'eau, contrairement au MS ou la
  // notification des non-retenus est manuelle.
  const nonRetenus = (operation.commandes || []).filter((c) => isRefuseOuExpire(c.id_status));

  // Mail d'expiration du non-retenu ouvert dans l'apercu (null = aucun).
  const [expireOuvert, setExpireOuvert] = useState<CommandeRang | null>(null);

  const organismeNom =
    (operation.organisme as { nom?: string } | undefined)?.nom || "l'acheteur";

  const expirationHtml = expireOuvert
    ? buildExpirationMailPreview({
        // `date` du template = la date d'expiration constatee, soit l'horodatage du passage au statut.
        date: expireOuvert.updated_at ? new Date(expireOuvert.updated_at).toLocaleDateString('fr-FR') : '',
        operationNom: operation.nom || "l'opération",
        organismeNom,
        // Le back envoie `operation_prix_total` : le total de l'OPERATION, pas celui du rang.
        montant: totalOffreArticles(formatOffreArticles(operation, Number(expireOuvert.rang))).toLocaleString(
          'fr-FR',
          { minimumFractionDigits: 2, maximumFractionDigits: 2 },
        ),
        dateEnvoi: expireOuvert.updated_at ? dateTime(expireOuvert.updated_at) : null,
      })
    : '';

  // Banniere d'ETAT, rendue en haut ET en bas de l'onglet — meme dispositif que le MS
  // (`stateBanners`, OperationMarche.tsx) : l'onglet est long, l'etat doit se lire sans remonter.
  // Definie UNE fois : deux copies de son JSX finiraient par se contredire.
  const stateBanner = (
    <Banner
      variant="success"
      filled
      // « accepté et signé » : vocabulaire unique pour le statut Valide(7), aligne sur la case
      // prestataire. Au statut 7 les deux signatures sont posees — dire seulement « accepté »
      // decrivait un etat anterieur.
      message={`Bon de commande accepté et signé par ${prestaNom}${accepteLe ? ` le ${accepteLe}` : ''}. Il est disponible ci-dessous. Les onglets de messagerie opérationnelle et d'évaluation du prestataire sont désormais actifs.`}
    />
  );

  // Bouton de telechargement defini UNE fois : il est rendu dans sa section ET dans la barre
  // d'actions du bas. Deux copies de JSX finiraient par diverger (libelle, icone, handler).
  // `secondary` = fond blanc : le telechargement n'est pas l'action qui fait avancer le workflow,
  // c'est l'orange de la barre d'actions qui porte cette valeur-la.
  const boutonTelecharger = (
    <Button
      value="Télécharger le bon de commande signé"
      variant="secondary"
      size="medium"
      icon={<PdfIcon />}
      onClick={handleDownload}
    />
  );

  return (
    <div className="consultation-bc bc-notif" ref={printRef}>
      {/* En tete d'onglet, AVANT les sections : l'etat du bon de commande prime sur son detail. */}
      {stateBanner}

      {/* Recap de preparation, comme cote MS ou il ouvre ce meme onglet. */}
      <OperationPreparationBC operation={operation} titulaire={commandeDuRang(operation, rang)} />

      <SectionPliable title="Signature et notification" defaultOpen>
        <div className="section-content">
          <div className="bc-notif__preview">
            {/* Preview a 3/4 de la largeur (`--narrow`), comme le MS : le reste est du vide, il laisse
                de quoi scroller la page autour du document. */}
            <div className="bc-notif__pdf bc-notif__pdf--narrow">
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  title="Prévisualisation du bon de commande signé"
                  className="bc-notif__iframe"
                />
              ) : pdfError ? (
                <div className="bc-notif__preview-empty">
                  La prévisualisation du bon de commande n'est pas disponible pour le moment. Vous
                  pouvez le télécharger ci-dessous.
                </div>
              ) : (
                <div className="bc-notif__preview-empty">Chargement de la prévisualisation…</div>
              )}
            </div>
          </div>

          {inlineError && (
            <div className="cmd-offre__inline-message cmd-offre__inline-message--error">
              {inlineError}
            </div>
          )}

          <div className="bc-notif__actions">
            {boutonTelecharger}
          </div>
        </div>
      </SectionPliable>

      <SectionPliable title="Information au candidat" defaultOpen>
        <div className="section-content">
          <div className="bc-notif__candidats">
            <div className="bc-notif__candidats-row bc-notif__candidats-head">
              <span>Prestataire (Rang {rang})</span>
              <span>Mail</span>
              <span>Contenu de la notification</span>
              <span>Notification</span>
            </div>
            <div className="bc-notif__candidats-row">
              <span className="bc-notif__candidats-nom">{prestaNom}</span>
              <span className="bc-notif__candidats-mail">{candidatMail}</span>
              <span>
                <button
                  type="button"
                  className="bc-notif__candidats-link"
                  onClick={() => setIsMailOpen(true)}
                >
                  Voir le message
                </button>
              </span>
              <span>{accepteLe ? `Notifié le ${accepteLe}` : '—'}</span>
            </div>

            {/* NON-RETENUS : un bloc a part, avec son propre en-tete — leur mail n'est pas la
                notification du bon de commande mais l'expiration (Brevo 13). Les melanger dans le
                meme tableau laisserait croire qu'ils ont recu la meme chose. */}
            {nonRetenus.length > 0 && (
              <>
                <div className="bc-notif__candidats-row bc-notif__candidats-head bc-notif__candidats-head--non-retenus">
                  <span>Prestataires non retenus</span>
                  <span>Mail</span>
                  <span>Contenu de la notification</span>
                  <span>Notification</span>
                </div>
                {nonRetenus.map((cmd) => {
                  const ent = cmd.contrat?.entite as
                    | { nom?: string; interlocuteur?: { email_contact?: string } }
                    | undefined;
                  return (
                    <div className="bc-notif__candidats-row" key={cmd.id ?? String(cmd.rang)}>
                      <span className="bc-notif__candidats-nom">
                        {ent?.nom || 'Prestataire'} (Rang {cmd.rang})
                      </span>
                      <span className="bc-notif__candidats-mail">
                        {ent?.interlocuteur?.email_contact || '—'}
                      </span>
                      <span>
                        <button
                          type="button"
                          className="bc-notif__candidats-link"
                          onClick={() => setExpireOuvert(cmd)}
                        >
                          Voir le message
                        </button>
                      </span>
                      <span>
                        {/* Le libelle suit le STATUT reel : "Expiré le" sur un rang refuse (8) serait
                            faux. Les deux recoivent le meme mail (13), pas le meme motif. */}
                        {cmd.updated_at
                          ? `${cmd.id_status === StatusId.Refuse ? 'Refusé' : 'Expiré'} le ${dateTime(cmd.updated_at)}`
                          : '—'}
                      </span>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </SectionPliable>

      <CommandesComplementaires operation={operation} />

      {/* Rappel de l'etat tout en bas. `no-print` : a l'impression il est deja en tete, le repeter
          n'aurait servi qu'a consommer du papier. Meme choix que le MS. */}
      <div className="no-print">{stateBanner}</div>


      {/* Barre d'actions de bas d'onglet. Elle n'existe que pour ce CTA : cet onglet n'a aucune
          action de workflow, le telechargement vivant dans sa section. `.bc-notif__actions` la
          cale a droite, comme la barre de la section signature. `no-print` : de la navigation
          n'a rien a faire sur un document imprime. */}
      {/* Barre d'actions de bas d'onglet. Rendue en PERMANENCE : elle porte l'impression et le
          telechargement, utiles quel que soit l'etat — le CTA de navigation, lui, se masque seul.
          Le telechargement y est DUPLIQUE (meme JSX que dans la section, defini une fois) : l'onglet
          est long, et on ne doit pas remonter pour recuperer le document. */}
      <div className="no-print bc-notif__actions">
        <PrintButton
          targetRef={printRef}
          fileName={`${operation.numero ?? operation.id} - Notification`}
          value="Imprimer la page"
          suiviHeaderProps={{
            titre: operation.nom || '',
            sousTitre: '',
            subtitle: `${operation.lot_technique?.id ?? ''} - ${operation.lot_technique?.nom ?? ''}`,
            status: operation.status?.nom || '',
            statusColor: getStatusColor(operation.status?.nom || ''),
          }}
        />
        {boutonTelecharger}
        {nextTab && (
          <NextTabButton label={nextTab.label} hidden={!nextTab.visible} onClick={nextTab.onClick} />
        )}
      </div>

      {/* Apercu du mail d'EXPIRATION d'un non-retenu (template Brevo 13). Meme habillage que l'apercu
          du mail de notification, mais son propre contenu et sa propre date d'envoi. */}
      {expireOuvert && (
        <div className="bc-notif__modal-overlay" onClick={() => setExpireOuvert(null)}>
          <div className="bc-notif__modal" onClick={(e) => e.stopPropagation()}>
            <div className="bc-notif__modal-bar">
              <span className="bc-notif__modal-title">
                {expireOuvert.updated_at
                  ? `Envoyé le ${dateTime(expireOuvert.updated_at)}`
                  : 'Aperçu du message'}
              </span>
              <div className="bc-notif__modal-actions">
                <Button
                  value="PDF"
                  variant="secondary"
                  size="small"
                  icon={<PdfIcon />}
                  onClick={() => handlePrintHtml(expirationHtml)}
                />
                <Button value="Fermer" variant="primary" size="small" onClick={() => setExpireOuvert(null)} />
              </div>
            </div>
            <MailPreview
              selectedCommandeForPreview={{ id: expireOuvert.id ?? externalId }}
              generateMailPreview={() => expirationHtml}
            />
          </div>
        </div>
      )}

      {/* Apercu du mail de notification (template Brevo 12) rendu dans une iframe sandbox. */}
      {isMailOpen && (
        <div className="bc-notif__modal-overlay" onClick={() => setIsMailOpen(false)}>
          <div className="bc-notif__modal" onClick={(e) => e.stopPropagation()}>
            <div className="bc-notif__modal-bar">
              <span className="bc-notif__modal-title">
                {accepteLe ? `Envoyé le ${accepteLe}` : 'Aperçu du message'}
              </span>
              <div className="bc-notif__modal-actions">
                <Button value="PDF" variant="secondary" size="small" icon={<PdfIcon />} onClick={() => handlePrintHtml(mailHtml)} />
                <Button value="Fermer" variant="primary" size="small" onClick={() => setIsMailOpen(false)} />
              </div>
            </div>
            <MailPreview
              selectedCommandeForPreview={{ id: externalId }}
              generateMailPreview={() => mailHtml}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationNotification;
